import { create } from 'zustand';

export type ChatUnavailableReason =
  | 'not_supported' // has_chat is false
  | 'workflow_not_running' // no current session
  | null;

export type ChatMode = 'node' | 'general' | 'workflow';
// NOTE: 'node' and 'general' are legacy values kept for the live node chat
// mode transition logic. The unified chat always uses 'workflow'.

export const GENERAL_CHAT_MODELS = [
  { id: 'claude-opus-4-6', label: 'Claude Opus 4.6', provider: 'Anthropic' },
  { id: 'gpt-5.4', label: 'GPT 5.4', provider: 'OpenAI' },
  { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', provider: 'Google' },
] as const;

export type GeneralChatModelId = (typeof GENERAL_CHAT_MODELS)[number]['id'];

interface ChatUIState {
  isChatConnecting: boolean;
  isChatConnected: boolean;
  isChatDenied: boolean;
  isChatAwaitingInput: boolean;
  chatUnavailableReason: ChatUnavailableReason;

  // General chat state
  chatMode: ChatMode;
  selectedModel: GeneralChatModelId;
  isGeneralChatActive: boolean;

  // Actions
  setChatConnecting: (connecting: boolean) => void;
  setChatConnected: (connected: boolean) => void;
  setChatDenied: (denied: boolean) => void;
  setChatAwaitingInput: (awaiting: boolean) => void;
  setChatUnavailable: (reason: ChatUnavailableReason) => void;
  setChatMode: (mode: ChatMode) => void;
  setSelectedModel: (model: GeneralChatModelId) => void;
  setGeneralChatActive: (active: boolean) => void;
  resetChatUI: () => void;
}

export const useChatUIStore = create<ChatUIState>((set) => ({
  // Initial state
  isChatConnecting: false,
  isChatConnected: false,
  isChatDenied: false,
  isChatAwaitingInput: false,
  chatUnavailableReason: null,

  // General chat state
  chatMode: 'general',
  selectedModel: 'claude-opus-4-6',
  isGeneralChatActive: false,

  // Actions
  setChatConnecting: (connecting) =>
    set({
      isChatConnecting: connecting,
      isChatConnected: false,
      chatUnavailableReason: null,
      isChatDenied: false,
    }),

  setChatConnected: (connected) =>
    set({
      isChatConnected: connected,
      isChatConnecting: false,
      isChatDenied: false,
    }),

  setChatDenied: (denied) =>
    set({
      isChatDenied: denied,
      isChatConnecting: false,
    }),

  setChatAwaitingInput: (awaiting) => set({ isChatAwaitingInput: awaiting }),

  setChatUnavailable: (reason) =>
    set({
      chatUnavailableReason: reason,
      isChatConnecting: false,
    }),

  setChatMode: (mode) => set({ chatMode: mode }),
  setSelectedModel: (model) => set({ selectedModel: model }),
  setGeneralChatActive: (active) => set({ isGeneralChatActive: active }),

  resetChatUI: () =>
    set({
      isChatConnecting: false,
      isChatConnected: false,
      isChatDenied: false,
      isChatAwaitingInput: false,
      chatUnavailableReason: null,
      // NOTE: do NOT reset isGeneralChatActive here — it's owned by the
      // general chat WS handlers and independent of the node chat lifecycle.
    }),
}));
